import { useState } from 'react';
import { HardDrive, Database, Server, Boxes, Star } from 'lucide-react';
import type { StorageClassInfo, NodeStorageInfo } from '@/types';
import { useClusterStorage, useClusterNamespaces } from './useClusterData';

interface Props {
  siteId: string;
}

const STATUS_OPTIONS = ['All', 'Bound', 'Pending', 'Lost'] as const;

function StatusBadge({ status }: { status: string }) {
  const cls = status === 'Bound' ? 'text-status-healthy bg-status-healthy/10'
    : status === 'Pending' ? 'text-status-warning bg-status-warning/10'
    : status === 'Lost' ? 'text-status-error bg-status-error/10'
    : 'text-silver-muted bg-surface-elevated';

  return <span className={`text-xs px-2 py-0.5 rounded font-medium ${cls}`}>{status}</span>;
}

function formatBytes(bytes: number): string {
  if (!bytes || bytes === 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(i > 2 ? 1 : 0)} ${units[i]}`;
}

/**
 * Best-effort parse of a Kubernetes resource quantity string into bytes.
 * Handles binary suffixes (Ki/Mi/Gi/Ti/Pi/Ei), decimal suffixes (k/M/G/T/P/E),
 * and plain numbers. Returns 0 if unparseable.
 */
function parseK8sQuantityBytes(qty: string): number {
  if (!qty) return 0;
  const m = qty.match(/^(\d+(?:\.\d+)?)([KMGTPE]i?)?$/);
  if (!m) return 0;
  const value = parseFloat(m[1]);
  const unit = m[2];
  const binMul: Record<string, number> = {
    Ki: 1024, Mi: 1024 ** 2, Gi: 1024 ** 3, Ti: 1024 ** 4, Pi: 1024 ** 5, Ei: 1024 ** 6,
  };
  const decMul: Record<string, number> = {
    K: 1e3, M: 1e6, G: 1e9, T: 1e12, P: 1e15, E: 1e18,
  };
  if (!unit) return value;
  return value * (binMul[unit] ?? decMul[unit] ?? 1);
}

function diskTypeBadge(type: string) {
  const cls = type === 'nvme' ? 'bg-blue-500/15 text-blue-300'
    : type === 'ssd' ? 'bg-status-healthy/15 text-status-healthy'
    : type === 'usb' ? 'bg-amber-500/15 text-amber-300'
    : 'bg-surface-elevated text-silver-muted';
  return <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-medium ${cls}`}>{type}</span>;
}

function StorageClassesSection({ items }: { items: StorageClassInfo[] }) {
  return (
    <div className="bg-surface border border-surface-elevated rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-surface-elevated flex items-center gap-2">
        <Boxes size={14} className="text-silver-muted" />
        <h3 className="text-sm font-medium text-silver-bright">Storage Classes</h3>
        <span className="text-xs text-silver-muted">({items.length})</span>
      </div>
      {items.length === 0 ? (
        <div className="text-center py-8 px-4 space-y-2">
          <Boxes size={32} className="mx-auto text-silver-muted/30" />
          <p className="text-silver-muted text-sm">No storage classes installed yet</p>
          <p className="text-silver-muted/60 text-xs">
            Run cluster bootstrap to install Longhorn / hostPath provisioners.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-surface-elevated">
                <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Name</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Provisioner</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Reclaim</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Binding</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Default</th>
              </tr>
            </thead>
            <tbody>
              {items.map((sc) => (
                <tr key={sc.name} className="border-b border-surface-elevated last:border-0 hover:bg-surface-elevated/30 transition-colors">
                  <td className="px-4 py-2.5 text-xs text-silver-bright font-mono">{sc.name}</td>
                  <td className="px-4 py-2.5 text-xs text-silver-muted font-mono">{sc.provisioner || '—'}</td>
                  <td className="px-4 py-2.5 text-xs text-silver-muted">{sc.reclaimPolicy || '—'}</td>
                  <td className="px-4 py-2.5 text-xs text-silver-muted">{sc.volumeBindingMode || '—'}</td>
                  <td className="px-4 py-2.5">
                    {sc.isDefault ? (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-gold/15 text-gold font-medium">
                        <Star size={10} />default
                      </span>
                    ) : (
                      <span className="text-xs text-silver-muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function NodeStorageSection({ items }: { items: NodeStorageInfo[] }) {
  return (
    <div className="bg-surface border border-surface-elevated rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-surface-elevated flex items-center gap-2">
        <Server size={14} className="text-silver-muted" />
        <h3 className="text-sm font-medium text-silver-bright">Node Storage</h3>
        <span className="text-xs text-silver-muted">({items.length})</span>
      </div>
      {items.length === 0 ? (
        <div className="text-center py-8 px-4 space-y-2">
          <Server size={32} className="mx-auto text-silver-muted/30" />
          <p className="text-silver-muted text-sm">No nodes reporting storage capacity</p>
        </div>
      ) : (
        <div className="divide-y divide-surface-elevated">
          {items.map((n) => {
            const capacityBytes = parseK8sQuantityBytes(n.capacityEphemeral);
            return (
              <div key={n.node} className="px-4 py-3 space-y-2">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Server size={14} className="text-silver-muted" />
                    <span className="text-sm font-mono text-silver-bright">{n.node}</span>
                  </div>
                  <div className="text-xs text-silver-muted">
                    Ephemeral capacity: <span className="text-silver-bright font-mono">{formatBytes(capacityBytes)}</span>
                  </div>
                </div>
                {n.disks.length === 0 ? (
                  <div className="text-xs text-silver-muted/70 pl-6">
                    Hardware discovery has not populated disks for this node.
                  </div>
                ) : (
                  <div className="grid gap-1 pl-6">
                    {n.disks.map((d) => (
                      <div key={d.name} className="flex items-center gap-2 text-xs text-silver-muted">
                        <HardDrive size={11} className="text-silver-muted/60" />
                        <span className="font-mono text-silver-bright">{d.name}</span>
                        <span className="text-silver-muted">·</span>
                        <span className="font-mono">{formatBytes(d.sizeBytes)}</span>
                        {diskTypeBadge(d.type)}
                        {d.model && <span className="text-silver-muted/70 truncate">{d.model}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function StoragePanel({ siteId }: Props) {
  const [namespace, setNamespace] = useState<string>('');
  const [status, setStatus] = useState<string>('All');

  const { data: namespaces } = useClusterNamespaces(siteId);
  const { data: storage, isLoading } = useClusterStorage(siteId, {
    namespace: namespace || undefined,
    status: status !== 'All' ? status : undefined,
  });

  if (isLoading && !storage) {
    return (
      <div className="bg-surface border border-surface-elevated rounded-xl">
        <div className="p-8 space-y-3 animate-pulse">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-8 bg-surface-elevated rounded" />
          ))}
        </div>
      </div>
    );
  }

  const storageClasses = storage?.storageClasses ?? [];
  const nodeStorage = storage?.nodeStorage ?? [];
  const pvcs = storage?.pvcs ?? [];

  return (
    <div className="space-y-4">
      <StorageClassesSection items={storageClasses} />

      <NodeStorageSection items={nodeStorage} />

      {/* Persistent Volume Claims — keep existing filters and table */}
      <div className="bg-surface border border-surface-elevated rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-surface-elevated flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Database size={14} className="text-silver-muted" />
            <h3 className="text-sm font-medium text-silver-bright">Persistent Volume Claims</h3>
            <span className="text-xs text-silver-muted">({pvcs.length})</span>
          </div>
          <div className="flex-1" />
          <select
            value={namespace}
            onChange={(e) => setNamespace(e.target.value)}
            className="bg-surface border border-surface-elevated rounded-lg px-3 py-1.5 text-xs text-silver-bright focus:border-gold focus:outline-none"
          >
            <option value="">All namespaces</option>
            {namespaces?.map((ns) => (
              <option key={ns} value={ns}>{ns}</option>
            ))}
          </select>

          <div className="flex gap-1">
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                  status === s
                    ? 'bg-gold/20 text-gold font-medium'
                    : 'text-silver-muted hover:text-silver-bright hover:bg-surface-elevated'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {pvcs.length === 0 ? (
          <div className="text-center py-12 space-y-2">
            <HardDrive size={40} className="mx-auto text-silver-muted/30" />
            <p className="text-silver-muted">No persistent volume claims found</p>
            <p className="text-silver-muted/60 text-xs">
              PVCs appear here once apps that need storage are deployed.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface-elevated">
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Name</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Namespace</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Status</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Capacity</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Access Modes</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Storage Class</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Volume</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Age</th>
                </tr>
              </thead>
              <tbody>
                {pvcs.map((pvc) => (
                  <tr key={`${pvc.namespace}/${pvc.name}`} className="border-b border-surface-elevated last:border-0 hover:bg-surface-elevated/30 transition-colors">
                    <td className="px-4 py-2.5 text-xs text-silver-bright font-mono">{pvc.name}</td>
                    <td className="px-4 py-2.5 text-xs text-silver-muted">{pvc.namespace}</td>
                    <td className="px-4 py-2.5"><StatusBadge status={pvc.status} /></td>
                    <td className="px-4 py-2.5 text-xs text-silver-bright font-mono">{pvc.capacity}</td>
                    <td className="px-4 py-2.5 text-xs text-silver-muted font-mono">{pvc.accessModes.join(', ')}</td>
                    <td className="px-4 py-2.5 text-xs text-silver-muted">{pvc.storageClass || '—'}</td>
                    <td className="px-4 py-2.5 text-xs text-silver-muted font-mono truncate max-w-[150px]">{pvc.volumeName || '—'}</td>
                    <td className="px-4 py-2.5 text-xs text-silver-muted">{pvc.age}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
